import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Button,
} from "@mui/material";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import { postJsonData } from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import AuthContext from "../../store/AuthContext";
import Loader from "../../component/loading-screen/Loader";
import SmallLoader from "../../component/loading-screen/SmallLoader";

const AddCompensatoryLeave = ({ setOpenModal, openModal, refresh }) => {
  const authCtx = useContext(AuthContext);
  const user = authCtx.user;

  const [newCompensatoryReq, setNewCompensatoryReq] = useState({
    request_nature: "earn",
    work_type: "", // Changed to lowercase to match backend
    from: "",
    to: "",
    reason: "",
  });

  const workTypes = [
    { value: "full_day", label: "Full Day" },
    { value: "morning_half", label: "Morning Half Day" },
    { value: "evening_half", label: "Evening Half Day" },
  ];

  const [image, setImage] = useState(null);
  const [request, setRequest] = useState(false);
  const [error, setError] = useState("");
  const [calculatedDays, setCalculatedDays] = useState(0);

  // Calculate days when from/to dates change
  useEffect(() => {
    if (newCompensatoryReq.from && newCompensatoryReq.to) {
      const fromDate = new Date(newCompensatoryReq.from);
      const toDate = new Date(newCompensatoryReq.to);

      // Calculate total days difference
      const timeDiff = Math.abs(toDate.getTime() - fromDate.getTime());
      const totalDays = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

      // Calculate compensatory days based on work type
      // Use same lowercase comparison
      const perDayValue = newCompensatoryReq.work_type === "full_day" ? 1 : 0.5;
      const calculated = totalDays * perDayValue;

      setCalculatedDays(calculated);
    } else {
      setCalculatedDays(0);
    }
  }, [
    newCompensatoryReq.from,
    newCompensatoryReq.to,
    newCompensatoryReq.work_type,
  ]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setNewCompensatoryReq((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    const requiredFields = ["from", "to", "work_type", "reason"];
    const isValid = requiredFields.every((key) => {
      const val = newCompensatoryReq[key];
      return typeof val === "string" ? val.trim() !== "" : val != null;
    });

    if (!isValid) {
      setError("All fields are required.");
      return;
    }

    // Check if 'from' date is before 'to' date
    const fromDate = new Date(newCompensatoryReq.from);
    const toDate = new Date(newCompensatoryReq.to);
    if (fromDate > toDate) {
      setError("'From' date must be before or equal to 'To' date.");
      return;
    }

    const formData = new FormData();

    // Add all fields from newCompensatoryReq
    Object.entries(newCompensatoryReq).forEach(([key, value]) => {
      formData.append(key, value);
    });

    formData.append("user_id", user.id);
    formData.append("emp_name", user.name);

    if (image) formData.append("img", image);

    postJsonData(
      ApiEndpoints.EARN_COMPENSATORY_LEAVE,
      formData,
      setRequest,
      (res) => {
        okSuccessToast(res.data.message);
        handleModalClose();
        if (refresh) refresh();
      },
      (err) => apiErrorToast(err),
      true // isMultipart for file upload
    );
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setNewCompensatoryReq({
      request_nature: "EARN",
      work_type: "", // Reset to lowercase
      from: "",
      to: "",
      reason: "",
    });
    setImage(null);
    setError("");
    setCalculatedDays(0);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "90%", // Extra small screens (phones)
      sm: "85%", // Small screens (tablets)
      md: "70%", // Medium screens
      lg: "600px", // Large screens (desktops)
      xl: "600px", // Extra large screens
    },
    maxWidth: "600px", // Maximum width
    maxHeight: "90vh", // Prevent modal from being too tall
    overflowY: "auto", // Add scroll if content is too tall
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: {
      xs: 2, // Less padding on small screens
      sm: 3, // More padding on medium screens
      md: 4, // Even more padding on larger screens
    },
    borderRadius: 2, // Rounded corners for better appearance
  };

  return (
    <Modal open={openModal} onClose={handleModalClose}>
      <Box sx={style}>
        <ModalHeader
          title="Request Compensatory Leave"
          handleClose={handleModalClose}
        />
        <SmallLoader loading={request} />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required size="small">
                <InputLabel>Work Type</InputLabel>
                <Select
                  name="work_type"
                  value={newCompensatoryReq.work_type}
                  onChange={handleFieldChange}
                  label="Work Type"
                >
                  {workTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Display calculated days */}
            {calculatedDays > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Calculated Compensatory Leave:</strong>{" "}
                    {calculatedDays} day{calculatedDays !== 1 ? "s" : ""}
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                label="Work From Date"
                type="date"
                name="from"
                fullWidth
                required
                onChange={handleFieldChange}
                value={newCompensatoryReq.from}
                InputLabelProps={{ shrink: true }}
                error={!!error && !newCompensatoryReq.from}
                helperText={
                  error && !newCompensatoryReq.from
                    ? "This field is required"
                    : ""
                }
                size="small"
                InputProps={{
                  sx: {
                    "& input": {
                      fontSize: { xs: "14px", sm: "16px" },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Work To Date"
                type="date"
                name="to"
                fullWidth
                required
                onChange={handleFieldChange}
                value={newCompensatoryReq.to}
                InputLabelProps={{ shrink: true }}
                error={!!error && !newCompensatoryReq.to}
                helperText={
                  error && !newCompensatoryReq.to
                    ? "This field is required"
                    : ""
                }
                size="small"
                InputProps={{
                  sx: {
                    "& input": {
                      fontSize: { xs: "14px", sm: "16px" },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Reason for Extra Work"
                name="reason"
                fullWidth
                required
                multiline
                rows={3}
                onChange={handleFieldChange}
                value={newCompensatoryReq.reason}
                error={!!error && !newCompensatoryReq.reason}
                helperText={
                  error && !newCompensatoryReq.reason
                    ? "This field is required"
                    : "Explain why you worked on these dates (e.g., project deadline, extra shift, weekend work)"
                }
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                >
                  <strong>Upload Proof (Optional)</strong>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <input
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                    id="compensatory_proof"
                    style={{
                      width: "100%",
                      padding: "8px 0",
                      fontSize: "14px",
                    }}
                  />
                </Box>
                {image && (
                  <Box sx={{ marginTop: 1 }}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        wordBreak: "break-word", // Prevent long filenames from breaking layout
                        fontSize: { xs: "13px", sm: "14px" },
                      }}
                    >
                      <strong>Selected Image:</strong> {image.name}
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                >
                  Upload screenshot of work proof, attendance sheet, or manager
                  approval
                </Typography>
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexDirection: {
                xs: "column-reverse", // Stack buttons vertically on mobile, Cancel on top
                sm: "row", // Side by side on larger screens
              },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleModalClose}
              fullWidth={{ xs: true, sm: false }} // Full width on mobile
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                !newCompensatoryReq.from ||
                !newCompensatoryReq.to ||
                !newCompensatoryReq.reason ||
                !newCompensatoryReq.work_type
              }
              fullWidth={{ xs: true, sm: false }} // Full width on mobile
            >
              Submit Request
            </Button>
          </Box>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
              {error}
            </Typography>
          </Alert>
        )}
      </Box>
    </Modal>
  );
};
export default AddCompensatoryLeave;
